import type { ImageAIProvider, ProviderInfo, PollResult } from '../../types/providers';
import type { OperationType } from '../../types/operations';

interface ReplicateModelConfig {
  // Community models: pin a specific version hash via "owner/name:hash"
  version?: string;
  // Official models: reference by slug only; server resolves latest version
  model?: string;
  buildInput: (
    imageDataUri: string,
    options?: Record<string, unknown>
  ) => Record<string, unknown>;
  estimatedCost: number;
}

const MODELS: Partial<Record<OperationType, ReplicateModelConfig>> = {
  upscale: {
    version: 'nightmareai/real-esrgan:f121d640bd286e1fdc67f9799164c1d5be36ff74576ee11c803ae5b665dd46aa',
    buildInput: (image, opts) => ({
      image,
      scale: opts?.scale ?? 4,
      face_enhance: opts?.face_enhance ?? false,
    }),
    estimatedCost: 0.01,
  },
  remove_bg: {
    version: 'cjwbw/rembg:fb8af171cfa1616ddcf1242c093f9c46bcada5ad4cf6f2fbe8b81b330ec5c003',
    buildInput: (image) => ({ image }),
    estimatedCost: 0.01,
  },
  inpaint: {
    version: 'stability-ai/stable-diffusion-inpainting:95b7223104132402a9ae91cc677285bc5eb997834bd2349fa486f53910fd68b3',
    buildInput: (image, opts) => ({
      image,
      mask: opts?.mask,
      prompt: opts?.prompt || 'clean background, seamless',
      num_outputs: 1,
    }),
    estimatedCost: 0.03,
  },
  face_variation: {
    // fofr/consistent-character — purpose-built for whole-person consistency across scenes
    // (headshots, full-body portraits, different poses/outfits/locations).
    // Takes a reference "subject" image; prompts describe the scene with no identity-marker token.
    version: 'fofr/consistent-character:9c77a3c2f884193fcee4d89645f02a0b9def9434f9e03cb98460456b831c8772',
    buildInput: (image, opts) => ({
      subject: image,
      prompt: opts?.prompt || 'a portrait photo of the character, natural lighting, professional photography',
      number_of_outputs: opts?.count ?? 2,
      negative_prompt: opts?.negative_prompt || 'deformed, distorted, disfigured, blurry, low quality',
      randomise_poses: opts?.randomise_poses ?? false,
      output_format: 'png',
      output_quality: 90,
    }),
    estimatedCost: 0.08,
  },
  outpaint: {
    // Official Replicate model — invoked via /v1/models/{slug}/predictions, no version pinning
    model: 'black-forest-labs/flux-fill-pro',
    buildInput: (image, opts) => ({
      image,
      mask: opts?.mask,
      prompt: opts?.prompt || 'extend this image naturally, seamless continuation',
      steps: 50,
      guidance: 3,
      output_format: 'png',
    }),
    estimatedCost: 0.05,
  },
};

async function blobToDataUri(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

export class ReplicateProvider implements ImageAIProvider {
  readonly info: ProviderInfo = {
    id: 'replicate',
    name: 'Replicate',
    tier: 'paid',
    supportedOps: ['upscale', 'remove_bg', 'inpaint', 'outpaint', 'face_variation', 'style_transfer'],
    requiresApiKey: true,
    apiKeyUrl: 'https://replicate.com/account/api-tokens',
  };

  private apiToken: string;
  private baseUrl = 'https://api.replicate.com/v1';
  private proxyUrl?: string;

  constructor(apiToken: string, proxyUrl?: string) {
    this.apiToken = apiToken;
    this.proxyUrl = proxyUrl;
  }

  private get fetchUrl(): string {
    return this.proxyUrl || this.baseUrl;
  }

  private get headers(): Record<string, string> {
    return {
      Authorization: `Bearer ${this.apiToken}`,
      'Content-Type': 'application/json',
      Prefer: 'wait',
    };
  }

  async healthCheck(): Promise<boolean> {
    try {
      const res = await fetch(`${this.fetchUrl}/predictions`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${this.apiToken}` },
      });
      return res.ok || res.status === 405;
    } catch {
      return false;
    }
  }

  async submit(
    operation: OperationType,
    inputImage: Blob,
    options?: Record<string, unknown>
  ): Promise<string> {
    const config = MODELS[operation];
    if (!config) throw new Error(`Replicate does not support operation: ${operation}`);

    const imageDataUri = await blobToDataUri(inputImage);
    const input = config.buildInput(imageDataUri, options);

    if (options?.mask instanceof Blob) {
      const maskDataUri = await blobToDataUri(options.mask);
      (input as Record<string, unknown>).mask = maskDataUri;
    }

    let url: string;
    let body: Record<string, unknown>;

    if (config.model) {
      url = `${this.fetchUrl}/models/${config.model}/predictions`;
      body = { input };
    } else if (config.version) {
      url = `${this.fetchUrl}/predictions`;
      body = {
        version: config.version.includes(':') ? config.version.split(':')[1] : config.version,
        input,
      };
    } else {
      throw new Error(`Model config for ${operation} has neither version nor model slug`);
    }

    const res = await fetch(url, {
      method: 'POST',
      headers: this.headers,
      body: JSON.stringify(body),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: res.statusText }));
      throw new Error((err as { detail?: string }).detail || `Replicate API error: ${res.status}`);
    }

    const prediction = await res.json() as { id: string; status: string; output?: string[] };
    return prediction.id;
  }

  async poll(predictionId: string): Promise<PollResult> {
    const res = await fetch(`${this.fetchUrl}/predictions/${predictionId}`, {
      headers: { Authorization: `Bearer ${this.apiToken}` },
    });

    if (!res.ok) {
      return { status: 'failed', error: `Poll failed: ${res.status}` };
    }

    const prediction = await res.json() as {
      status: string;
      output?: string | string[];
      error?: string;
      logs?: string;
    };

    const output = prediction.output
      ? Array.isArray(prediction.output) ? prediction.output : [prediction.output]
      : undefined;

    return {
      status: prediction.status as PollResult['status'],
      output,
      error: prediction.error ?? undefined,
    };
  }

  async cancel(predictionId: string): Promise<void> {
    await fetch(`${this.fetchUrl}/predictions/${predictionId}/cancel`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${this.apiToken}` },
    }).catch(() => {});
  }

  getEstimatedCost(operation: OperationType): number {
    return MODELS[operation]?.estimatedCost ?? 0;
  }
}
